import { useCallback, useMemo, useState } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { DeleteIcon, DownloadIcon, InfoOutlinedIcon, ModeEditIcon } from "@l2r-front/l2r-icons"
import { useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { CircularProgress, L2RButton, MoreMenu, UiModal } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../constants/i18n"
import { API_MODULE_DOCUMENT } from "../../constants/modules"
import { useDocument } from "../../hooks/queries/useDocument"
import { useDocumentTypes } from "../../hooks/queries/useDocumentTypes"
import { downloadDocument } from "../../utils/documentUtils"

import * as Styled from "./DocumentReaderModal.styled"

export const DocumentReaderModal = (props) => {

    const {
        className,
        document,
        open,
        onClose,
        onDelete,
        onEdit,
    } = props

    const [infoPopupDisplayed, setInfoPopupDisplayed] = useState(false)

    const { t } = useTranslation(I18N_NAMESPACE)
    const { data, isLoading } = useDocument(document.uuid)
    const { data: documentTypes } = useDocumentTypes()
    const readOnly = useRoleIsReadOnly(data?.sub_networks, API_MODULE_DOCUMENT)

    const handleDownload = useCallback(async () => {
        await downloadDocument(data)
    }, [data])

    const handleInfoMenuClicked = useCallback(() => {
        setInfoPopupDisplayed(true)
    }, [setInfoPopupDisplayed])

    const documentType = useMemo(() => {
        if (!data?.document_type || !documentTypes) {
            return null
        }
        return documentTypes?.find(type => type.name === data.document_type)
    }, [data?.document_type, documentTypes])

    const buttons = useMemo(() => {
        return <Styled.ButtonsContainer>
            <L2RButton size="large" startIcon={<DownloadIcon />} onClick={handleDownload}>{t(I18N_NAMESPACE, "containers.documentReaderModal.download")}</L2RButton>
            <MoreMenu
                color="secondary"
                direction="horizontal"
                size="large"
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                menuConfig={[
                    {
                        displayed: true,
                        id: "info-document",
                        label: t(I18N_NAMESPACE, "components.documentReaderModal.menu.info"),
                        icon: <InfoOutlinedIcon color="common/black_states" />,
                        props: {
                            onClick: () => handleInfoMenuClicked(),
                        },
                    },
                    {
                        displayed: !readOnly,
                        id: "edit-document",
                        label: t(I18N_NAMESPACE, "containers.documentList.itemMenu.edit"),
                        icon: <ModeEditIcon color="common/black_states" />,
                        props: {
                            onClick: () => onEdit(data),
                        },
                    },
                    {
                        displayed: !readOnly,
                        id: "delete-document",
                        label: t(I18N_NAMESPACE, "containers.documentList.itemMenu.delete"),
                        icon: <DeleteIcon color="common/black_states" />,
                        props: {
                            onClick: () => onDelete(data),
                        },
                    },
                ]}
            />
        </Styled.ButtonsContainer>
    }, [
        data,
        handleDownload,
        handleInfoMenuClicked,
        onDelete,
        onEdit,
        readOnly,
        t,
    ])

    return <UiModal
        id="document-reader-modal"
        className={className}
        buttons={buttons}
        height="80%"
        width="90%"
        open={open}
        onClose={onClose}
        title={data?.filename}
        titleIcon={documentType?.icon && <Styled.TypeIcon src={documentType.icon} alt={documentType.name} />}
    >
        <Styled.Content>
            {isLoading ? <CircularProgress />
                : <Styled.FileViewer file={data} allowZoom variant="large" />}
            {infoPopupDisplayed && <Styled.DocumentInfo document={data} onClose={() => setInfoPopupDisplayed(false)} />}
        </Styled.Content>
    </UiModal >
}

DocumentReaderModal.propTypes = {
    className: PropTypes.string,
    document: PropTypes.object.isRequired,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
}